import type { DirectorLanguage, DirectorMoment, DirectorResponse, DirectorVisual, GameEvent } from '@clutch/core';
import { pick } from './i18n';

/** A single verified event, not the difference between editorially selected snapshots. */
export function momentVisual(d: DirectorResponse, moment: DirectorMoment | undefined, plays: readonly GameEvent[] = []): DirectorVisual | undefined {
  if (!moment || moment.kind === 'call' || moment.kind === 'reaction' || moment.seq > d.asOf.seq) return undefined;
  const visual = moment.visual ?? plays.find((p) => p.gameId === d.game.gameId && p.seq === moment.seq && moment.eventIds.includes(p.eventId));
  if (!visual || !moment.eventIds.includes(visual.eventId) || !d.provenance.eventIds.includes(visual.eventId)
    || !visual.eventId.startsWith(`${d.game.gameId}:`) || visual.seq !== moment.seq
    || visual.period !== moment.period || visual.clockSec !== moment.clockSec
    || visual.homeScore !== moment.homeScore || visual.awayScore !== moment.awayScore
    || moment.player && visual.personId !== undefined && visual.personId !== moment.player.personId) return undefined;
  return visual;
}

export function locatedShot(v: DirectorVisual | undefined): v is DirectorVisual & { x: number; y: number } {
  return !!v && v.type === 'shot' && typeof v.x === 'number' && Number.isFinite(v.x) && v.x >= 0 && v.x <= 100
    && typeof v.y === 'number' && Number.isFinite(v.y) && v.y >= 0 && v.y <= 100;
}

/** Impact is the points on this exact action. No score deltas across skipped moments. */
export function scoringSide(v: DirectorVisual | undefined) {
  return v && (v.type === 'shot' || v.type === 'freethrow') && v.made === true && typeof v.points === 'number' && v.points > 0 ? v.side : undefined;
}

const REASONS: Record<string, { en: string; hi: string; zh?: string }> = {
  own_confirmed_call: { en: 'Your locked call', hi: 'आपकी लॉक की गई कॉल' },
  required_climax: { en: 'The decisive finish', hi: 'निर्णायक आख़िरी पल' },
  followed_player: { en: 'A player you follow', hi: 'आपके चुने खिलाड़ी का पल' },
  followed_team: { en: 'Your team, in the moment', hi: 'इस पल आपकी टीम' },
  unseen_since_acknowledgement: { en: 'Since your last catch-up', hi: 'आपके पिछले अपडेट के बाद' },
  observed_comeback: { en: 'The comeback takes shape', hi: 'वापसी का अहम पल' },
  lead_change: { en: 'The lead changes hands', hi: 'बढ़त दूसरी टीम के पास' },
  unanswered_run: { en: 'A run without an answer', hi: 'लगातार अंक, कोई जवाब नहीं' },
  late_close_scoring: { en: 'Late points in a close game', hi: 'करीबी मैच के आख़िरी अंक' },
  late_close_miss: { en: 'A late chance in a close game', hi: 'करीबी मैच का आख़िरी मौका' },
  own_reaction: { en: 'Your reaction belongs here', hi: 'इस पल आपकी प्रतिक्रिया' },
  verified_game_end: { en: 'The verified final', hi: 'दर्ज अंतिम नतीजा' },
};
const ZH_REASONS: Record<string, string> = {
  own_confirmed_call: '你锁定的预测', required_climax: '关键收尾', followed_player: '你关注的球员',
  followed_team: '此刻，你的球队', unseen_since_acknowledgement: '上次回顾之后', observed_comeback: '追分的关键时刻',
  lead_change: '领先易主', unanswered_run: '对手尚未回应的得分潮', late_close_scoring: '胶着比赛后段的得分',
  late_close_miss: '胶着比赛后段的机会', own_reaction: '你在这一刻的反应', verified_game_end: '已核实的最终结果',
};

/** Only known, evidence-supported audit keys become fan-facing copy. Never arbitrary model text. */
export function momentReason(d: DirectorResponse, moment: DirectorMoment | undefined, language: DirectorLanguage): string | undefined {
  if (!moment) return undefined;
  const audit = d.editorial?.selected.find((m) => m.momentId === moment.momentId);
  const reasons = (audit?.reasonKeys ?? moment.reasons).filter((key) => moment.reasons.includes(key));
  const key = Object.keys(REASONS).find((k) => reasons.includes(k));
  if (!key) return undefined;
  if (key === 'required_climax' && d.asOf.status !== 'final') return pick({ en: 'The latest late-game plays', hi: 'मैच के आख़िरी हिस्से के हाल के प्ले', zh: '截至此刻的末节回合' }, language);
  const author = d.audience?.author;
  if (author) {
    if (key === 'own_confirmed_call') return pick({ en: `${author}’s locked call`, hi: `${author} की लॉक की गई कॉल`, zh: `${author}锁定的预测` }, language);
    if (key === 'followed_player') return pick({ en: `A player ${author} follows`, hi: `${author} के चुने खिलाड़ी का पल`, zh: `${author}关注的球员` }, language);
    if (key === 'followed_team') return pick({ en: `${author}’s team, in the moment`, hi: `इस पल ${author} की टीम`, zh: `此刻，${author}的球队` }, language);
    if (key === 'unseen_since_acknowledgement') return pick({ en: `Since ${author}’s last catch-up`, hi: `${author} के पिछले अपडेट के बाद`, zh: `${author}上次回顾之后` }, language);
    if (key === 'own_reaction') return pick({ en: `${author}’s reaction belongs here`, hi: `इस पल ${author} की प्रतिक्रिया`, zh: `${author}在这一刻的反应` }, language);
  }
  return pick({ ...REASONS[key]!, zh: ZH_REASONS[key]! }, language);
}
